import { ProgramState, SwimlaneItem } from '../../forces/assetDetails/assetDetails.types';
import { getProgramStateLabel, joinLabelSegments } from '../../utils/a11y/ariaLabel.utils';
import { formatTime } from '../../utils/date/date.utils';
import { RequiredProps } from '../../utils/types/typeUtils';

export interface SwimlaneItemAriaLabelOptions {
  showImdbRating?: 'onHover' | 'always' | 'never';
  ageLimitReplacesImdb?: boolean;
  /** `false` for items that put the title in the artwork instead (SuperWide). */
  showSubtitle?: boolean;
  /** `false` for swimlanes without an image overlay (MyList). */
  showProgramState?: boolean;
  showProviderLogos?: boolean;
  /** `true` for items with a progress bar (ContinueWatching). */
  showProgress?: boolean;
  showAgeLimit?: boolean;
}

/**
 * Builds the accessible name for a swimlane item.
 *
 * The whole card is a single link, so without an `aria-label` the name is the concatenation of every
 * descendant in DOM order – which puts the image overlays (provider logos, age limit, price) before
 * the title. This puts the item's own information first instead.
 *
 * The title comes first and verbatim, so the accessible name contains the visible label
 * (WCAG 2.5.3). Only information that is actually rendered is included, hence the options – but no
 * attempt is made to mirror the responsive breakpoints, an item is announced identically at every
 * viewport.
 */
export const buildSwimlaneItemAriaLabel = (item: SwimlaneItem, options: SwimlaneItemAriaLabelOptions = {}): string => {
  const {
    showImdbRating = 'always',
    ageLimitReplacesImdb = false,
    showSubtitle = true,
    showProgramState = true,
    showProviderLogos = true,
    showProgress = false,
    showAgeLimit = false,
  } = options;

  const ageLimitPillIsVisible = showAgeLimit && item.ageLimit != null;
  const announceImdbRating =
    showImdbRating !== 'never' && item.imdbRating != null && !(ageLimitReplacesImdb && ageLimitPillIsVisible);
  /* the subtitle is the wider source, but only when it is rendered at all */
  const subtitleAgeLimit = showSubtitle ? getSubtitleAgeLimit(item.subtitle) : undefined;
  const ageLimit = ageLimitPillIsVisible ? item.ageLimit : subtitleAgeLimit;
  const providerNames = item.logos.map(({ name }) => name);
  const progressPercent = Math.round((item.userData?.progressRatio ?? 0) * 100);
  const announceProgress = showProgress && progressPercent > 0;

  return joinLabelSegments([
    item.title,
    showSubtitle ? getSpokenSubtitle(item.subtitle) : undefined,
    /* e.g. "S1E10" – this is which episode the card is for, so it belongs with the title */
    showProgress ? item.userData?.progressLabel : undefined,
    showProgramState ? getProgramStateLabel(item.programState) : undefined,
    /* same wording as the sr-only text in `ImdbBadge` */
    announceImdbRating ? `${item.imdbRating} i vurdering på IMDb` : undefined,
    ageLimit != null ? `Aldersgrense ${ageLimit} år` : undefined,
    item.rightBottomLabel,
    /* the bar itself is purely visual, so the percentage only exists here – spelled out, since whether
       "%" is announced at all depends on the user's symbol verbosity settings */
    announceProgress ? `Sett ${progressPercent} prosent` : undefined,
    /* same wording as the sr-only text in `StackedProviderImage` */
    showProviderLogos && providerNames.length > 0 ? `Tilgjengelig på: ${providerNames.join(', ')}` : undefined,
  ]);
};

/**
 * Builds the accessible name for a live channel card ("På TV nå").
 **/

export const buildLiveChannelItemAriaLabel = (item: RequiredProps<SwimlaneItem, 'channel'>): string =>
  joinLabelSegments([item.channel.name, item.title, getBroadcastTimeLabel(item.programState)]);

/**
 * The visible tag shows a pre-formatted time range from the backend, which screen readers read out
 * inconsistently. The times themselves are on the program state, so the range is spelled out here
 * instead. Whole hours drop their minutes, matching how the time is said out loud.
 */
const getBroadcastTimeLabel = (state: ProgramState): string | undefined =>
  state.type === 'Live' ? `Fra klokken ${getSpokenTime(state.from)} til ${getSpokenTime(state.to)}` : undefined;

const getSpokenTime = (date: string): string => {
  const [hours, minutes] = formatTime(date).split(':');
  /* Number() drops the leading zero – "09" would otherwise be read out digit by digit */
  return minutes === '00' ? `${Number(hours)}` : `${Number(hours)}.${minutes}`;
};

/**
 * The subtitle is a pre-formatted string from the backend, e.g. `"2017 · 15+ · Drama"`. It has to stay
 * exactly as delivered visually, but `"15+"` is not an acceptable announcement, so we lift the age
 * limit out of the subtitle here and announce it as "Aldersgrense 15 år" instead.
 */
const AGE_LIMIT_SEGMENT = /^(\d{1,2})\+$/;
const SUBTITLE_SEPARATOR = '·';

const getSubtitleSegments = (subtitle: string): string[] =>
  subtitle
    .split(SUBTITLE_SEPARATOR)
    .map(segment => segment.trim())
    .filter(segment => segment !== '');

const getSubtitleAgeLimit = (subtitle: string | undefined): number | undefined => {
  if (!subtitle) {
    return undefined;
  }
  const ageLimit = getSubtitleSegments(subtitle)
    .map(segment => AGE_LIMIT_SEGMENT.exec(segment)?.[1])
    .find(match => match != null);

  return ageLimit != null ? Number(ageLimit) : undefined;
};

/** The subtitle without its age limit segment, joined so screen readers pause between the parts. */
const getSpokenSubtitle = (subtitle: string | undefined): string | undefined => {
  if (!subtitle) {
    return undefined;
  }
  const segments = getSubtitleSegments(subtitle).filter(segment => !AGE_LIMIT_SEGMENT.test(segment));

  return segments.length > 0 ? segments.join(', ') : undefined;
};
